// [Input] [Input] [Input]  | <New> <Member discount>
// <Save> <Cancel>          |
//

import { useState } from "react";
import { useAwaitResponse } from "../../../Api/HttpRequestHelpers";
import { CompetitionClient } from "../../../Api/client/CompetitionClient";
import { Discipline_GET } from "../../../Api/dto/Discipline_GET";
import {
  DiscountDto_GET,
  DiscountDto_PUT,
} from "../../../Api/dto/DiscountDtos";
import {
  Button,
  Center,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MultiSelect } from "react-multi-select-component";

const MEMBER_DISCOUNT_NAME = "MEDLEMSRABATT";

// name percent count <delete>
export function DiscountsEditView(props: {
  competitionId: number;
  disciplines: Discipline_GET[];
  discountUpdates: DiscountDto_PUT[];
  setDiscountUpdates: React.Dispatch<React.SetStateAction<DiscountDto_PUT[]>>;
}) {
  const [existingDiscounts, isLoadingDiscounts, setExistingDiscounts] =
    useAwaitResponse(
      { discounts: [] } as DiscountDto_GET,
      () => CompetitionClient.admin.getDiscounts(props.competitionId),
      (response) => response.body ?? ({ discounts: [] } as DiscountDto_GET)
    );

  const [discountToEdit, setDiscountToEdit] = useState<
    DiscountDto_PUT | undefined
  >(undefined);

  return (
    <VStack marginTop={"30px"} align={"left"} spacing={4}>
      <Center>
        <HStack hidden={discountToEdit != null}>
          <Button
            colorScheme="green"
            onClick={() => {
              setDiscountToEdit({
                name: "",
                percentage: 10,
                count: 1,
                disciplineIds: props.disciplines.map((d) => d.id),
              } as DiscountDto_PUT);
            }}
          >
            Ny rabatt
          </Button>
          <Button
            colorScheme="yellow"
            onClick={() => {
              let existingMemberDiscount = props.discountUpdates.find(
                (d) => d.name === MEMBER_DISCOUNT_NAME
              );
              if (existingMemberDiscount != null) {
                setDiscountToEdit({ ...existingMemberDiscount });
                return;
              }

              existingMemberDiscount = existingDiscounts.discounts.find(
                (d) => d.name === MEMBER_DISCOUNT_NAME
              );
              if (existingMemberDiscount != null) {
                setDiscountToEdit({ ...existingMemberDiscount });
                return;
              }

              setDiscountToEdit({
                name: MEMBER_DISCOUNT_NAME,
                count: 1,
                disciplineIds: props.disciplines.map((d) => d.id),
              } as DiscountDto_PUT);
            }}
          >
            Medlemsrabatt
          </Button>
        </HStack>
      </Center>

      <VStack hidden={discountToEdit == null}>
        {/* input:kod, input: procent, input: antal, multiInput: disciplines */}
        <HStack>
          <InputGroup
            background={"white"}
            borderRadius={"10px"}
            maxWidth={"fit-content"}
          >
            <InputLeftAddon>Kod:</InputLeftAddon>
            <Input
              placeholder="Rabatt kod"
              value={discountToEdit?.name ?? ""}
              contentEditable={discountToEdit?.name !== MEMBER_DISCOUNT_NAME}
              disabled={discountToEdit?.name === MEMBER_DISCOUNT_NAME}
              onChange={(e) => {
                const newVal = e.target.value.replace(/\s/g, "").toLowerCase();
                setDiscountToEdit((old) => {
                  return old == undefined
                    ? undefined
                    : { ...old, name: newVal };
                });
              }}
            />
          </InputGroup>
          <InputGroup
            background={"white"}
            borderRadius={"10px"}
            maxWidth={"150px"}
          >
            <Input
              placeholder="Rabatt i procent"
              value={
                discountToEdit?.percentage == -1
                  ? ""
                  : discountToEdit?.percentage
              }
              onChange={(e) =>
                setDiscountToEdit((old) => {
                  let newValue = -1;
                  if (e.target.value?.match(/^[\d]+$/)) {
                    newValue = Number(e.target.value);
                  }
                  return old == undefined
                    ? undefined
                    : {
                        ...old,
                        percentage: Math.max(-1, Math.min(100, newValue)),
                      };
                })
              }
            />
            <InputRightAddon>%</InputRightAddon>
          </InputGroup>
          <InputGroup
            background={"white"}
            borderRadius={"10px"}
            maxWidth={"fit-content"}
          >
            <Input
              placeholder="Antal"
              maxWidth={"100px"}
              contentEditable={discountToEdit?.name !== MEMBER_DISCOUNT_NAME}
              disabled={discountToEdit?.name === MEMBER_DISCOUNT_NAME}
              value={discountToEdit?.count == -1 ? "" : discountToEdit?.count}
              onChange={(e) => {
                setDiscountToEdit((old) => {
                  let newValue = -1;
                  if (e.target.value?.match(/^[\d]+$/)) {
                    newValue = Number(e.target.value);
                  }
                  return old == undefined
                    ? undefined
                    : { ...old, count: Math.max(-1, newValue) };
                });
              }}
            />
            <InputRightAddon>st</InputRightAddon>
          </InputGroup>
          <MultiSelect
            options={props.disciplines.map((d) => {
              return { label: d.name, value: d.id.toString() };
            })}
            value={
              discountToEdit?.disciplineIds.map((id) => {
                return {
                  value: id.toString(),
                  label:
                    props.disciplines.find((d) => d.id === id)?.name ?? "?",
                };
              }) ?? []
            }
            onChange={(selections: Array<{ value: string; label: string }>) => {
              setDiscountToEdit((old) => {
                return old === undefined
                  ? undefined
                  : {
                      ...old,
                      disciplineIds: selections.map((s) => parseInt(s.value)),
                    };
              });
            }}
            labelledBy="Välj grenar/stargrupper"
          />
        </HStack>
        {/* <OK> <Avbryt> */}
        <HStack>
          <Button
            colorScheme="green"
            isDisabled={
              discountToEdit?.name == null ||
              discountToEdit?.percentage == null ||
              discountToEdit?.count == null ||
              discountToEdit.name.length < 3 ||
              discountToEdit.percentage < 0 ||
              discountToEdit.percentage > 100 ||
              discountToEdit.count < 0
            }
            onClick={() => {
              props.setDiscountUpdates((old) => {
                return [
                  {
                    count: discountToEdit?.count,
                    disciplineIds: discountToEdit?.disciplineIds,
                    name: discountToEdit?.name,
                    percentage: discountToEdit?.percentage,
                  } as DiscountDto_PUT,
                  ...old.filter((d) => d.name !== discountToEdit?.name),
                ];
              });
              setExistingDiscounts((old) => {
                return {
                  discounts: old.discounts.filter(
                    (d) => d.name !== discountToEdit?.name
                  ),
                };
              });
              setDiscountToEdit(undefined);
            }}
          >
            OK
          </Button>
          <Button
            colorScheme="red"
            onClick={() => setDiscountToEdit(undefined)}
          >
            Avbryt
          </Button>
        </HStack>
      </VStack>

      <VStack align={"left"} marginLeft={"20px"}>
        <Text
          hidden={props.discountUpdates.length == 0}
          marginTop={"30px"}
          fontSize={"2xl"}
          textAlign={"center"}
        >
          Nya/uppdaterade (Klicka på Spara för att verkställa)
        </Text>
        {props.discountUpdates.map((discount) => {
          return (
            <HStack
              key={discount.name}
              padding={"10px"}
              borderWidth={"1px"}
              borderRadius={"md"}
              borderColor={"black"}
              shadow={"lg"}
              background={
                discount.name === discountToEdit?.name
                  ? "yellow.200"
                  : "green.200"
              }
            >
              <Text>
                <b>Kod:</b> {discount.name}
              </Text>
              <Text>
                <b>Procent:</b>{" "}
                {discount.name === discountToEdit?.name &&
                discountToEdit.percentage != -1
                  ? discountToEdit.percentage
                  : discount.percentage}{" "}
                {" %"}
              </Text>
              <Text>
                <b>Antal:</b>{" "}
                {discount.name === discountToEdit?.name &&
                discountToEdit.count != -1
                  ? discountToEdit.count
                  : discount.count}{" "}
                {" st"}
              </Text>
              <Text
                hidden={
                  discount.name === discountToEdit?.name
                    ? discountToEdit.disciplineIds.length == 0
                    : discount.disciplineIds.length == 0
                }
              >
                <b>Gäller för:</b>{" "}
                {discount.name === discountToEdit?.name
                  ? discountToEdit.disciplineIds
                      .map(
                        (id) => props.disciplines.find((d) => d.id == id)?.name
                      )
                      .join(", ")
                  : discount.disciplineIds
                      .map(
                        (id) => props.disciplines.find((d) => d.id == id)?.name
                      )
                      .join(", ")}
              </Text>
              <Text
                hidden={
                  !(discount.name === discountToEdit?.name
                    ? discountToEdit.disciplineIds.length == 0
                    : discount.disciplineIds.length == 0)
                }
              >
                <b>Gäller för:</b> Alla grenar/startgrupper
              </Text>
            </HStack>
          );
        })}
        <Text
          hidden={existingDiscounts.discounts.length == 0}
          marginTop={"30px"}
          fontSize={"2xl"}
          textAlign={"center"}
        >
          Existerande (oförändrade) rabatter
        </Text>
        {existingDiscounts.discounts.map((discount) => {
          return (
            <HStack
              padding={"10px"}
              borderWidth={"1px"}
              borderRadius={"md"}
              borderColor={"black"}
              shadow={"lg"}
              background={
                discount.name === discountToEdit?.name
                  ? "yellow.200"
                  : undefined
              }
            >
              <Text>
                <b>Kod:</b> {discount.name}
              </Text>
              <Text>
                <b>Procent:</b>{" "}
                {discount.name === discountToEdit?.name
                  ? discountToEdit.percentage
                  : discount.percentage}{" "}
                {" %"}
              </Text>
              <Text>
                <b>Antal:</b>{" "}
                {discount.name === discountToEdit?.name
                  ? discountToEdit.count
                  : discount.count}{" "}
                {" st"}
              </Text>
              <Text
                hidden={
                  discount.name === discountToEdit?.name
                    ? discountToEdit.disciplineIds.length == 0
                    : discount.disciplineIds.length == 0
                }
              >
                <b>Gäller för:</b>{" "}
                {discount.name === discountToEdit?.name
                  ? discountToEdit.disciplineIds.map(
                      (id) => props.disciplines.find((d) => d.id == id)?.name
                    )
                  : discount.disciplineIds
                      .map(
                        (id) => props.disciplines.find((d) => d.id == id)?.name
                      )
                      .join(", ")}{" "}
              </Text>
              <Text
                hidden={
                  !(discount.name === discountToEdit?.name
                    ? discountToEdit.disciplineIds.length == 0
                    : discount.disciplineIds.length == 0)
                }
              >
                <b>Gäller för:</b> Alla grenar/startgrupper
              </Text>
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
}
